import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Landmark } from 'lucide-react';
import { Card, CardHeader } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { financeReportsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { formatDate, formatMoney, formatRate } from '@/utils/format';
import { BarRow, Money, PaymentStatusBadge, Stat } from './shared';

/**
 * The financial position at a glance.
 *
 * <p>Four figures a bursar checks daily, then the four comparisons that explain
 * them. Nothing here computes anything: every number was aggregated by the
 * database, and the collection rate is shown as an em dash rather than 0% when
 * nothing has been billed — "we have collected 0% of nothing" is not a fact
 * about a school's finances.
 */
export function FinanceDashboardPage() {
  const context = useWorkingContext();
  const params = {
    ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.finance.dashboard(params),
    queryFn: () => financeReportsApi.dashboard(params),
  });

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (query.isPending) {
    return <LoadingState label="Loading the financial position" />;
  }

  const d = query.data;
  const currency = d.currencyCode;
  const maxMonth = Math.max(...d.monthlyCollections.map((m) => m.amount), 0);
  const maxClass = Math.max(...d.outstandingByClass.map((c) => c.outstanding), 0);
  const maxCategory = Math.max(...d.billedByCategory.map((c) => c.billed), 0);
  const maxMethod = Math.max(...d.paymentMethods.map((m) => m.amount), 0);

  return (
    <div>
      <PageHeader
        title="Fee Dashboard"
        description={`Billing and collection across the working academic year, as at ${formatDate(d.asOf)}.`}
        icon={<Landmark className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Billed" value={formatMoney(d.totalBilled, currency)}
              hint={`${d.invoiceCount} invoices issued`} />
        <Stat label="Collected" value={formatMoney(d.totalCollected, currency)}
              tone="success"
              hint={`Collection rate ${formatRate(d.collectionRate)}`} />
        <Stat label="Outstanding" value={formatMoney(d.totalOutstanding, currency)}
              hint={d.creditHeld > 0
                ? `${formatMoney(d.creditHeld, currency)} held on account`
                : 'No money held on account'} />
        <Stat label="Overdue" value={formatMoney(d.overdueAmount, currency)}
              tone={d.overdueAmount > 0 ? 'danger' : undefined}
              hint={d.overdueAmount > 0 ? 'Past the due date' : 'Nothing is past due'} />
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Today" value={formatMoney(d.collectedToday, currency)} />
        <Stat label="This month" value={formatMoney(d.collectedThisMonth, currency)} />
        <Stat label="Relief granted"
              value={formatMoney(d.totalDiscounts + d.totalScholarships, currency)}
              hint={`${formatMoney(d.totalScholarships, currency)} of it in awards`} />
        <Stat label="Late fees charged" value={formatMoney(d.totalLateFees, currency)}
              hint={d.draftInvoiceCount > 0
                ? `${d.draftInvoiceCount} invoices still in draft`
                : 'No invoices in draft'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Collections by month"
                      description={`Amounts in ${currency}, by the date money was taken.`} />
          <div className="px-4 py-3">
            {d.monthlyCollections.length === 0 ? (
              <EmptyState title="Nothing collected yet"
                          description="Collections appear here once payments are recorded." />
            ) : (
              d.monthlyCollections.map((month) => (
                <BarRow key={`${month.year}-${month.month}`} label={month.label}
                        sublabel={`${month.count} payment${month.count === 1 ? '' : 's'}`}
                        value={month.amount} max={maxMonth}
                        formatted={formatMoney(month.amount, currency)} />
              ))
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Outstanding by class"
                      description="Where the arrears are, worst first." />
          <div className="px-4 py-3">
            {d.outstandingByClass.length === 0 ? (
              <EmptyState title="Nothing outstanding"
                          description="Every issued invoice has been settled." />
            ) : (
              d.outstandingByClass.map((cls) => (
                <BarRow key={cls.gradeOfferingId ?? cls.gradeName} label={cls.gradeName}
                        sublabel={`${cls.studentCount} student${cls.studentCount === 1 ? '' : 's'}`}
                        value={cls.outstanding} max={maxClass}
                        formatted={formatMoney(cls.outstanding, currency)} />
              ))
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Billed by fee category"
                      description="What the school charges for." />
          <div className="px-4 py-3">
            {d.billedByCategory.length === 0 ? (
              <EmptyState title="Nothing billed yet" />
            ) : (
              d.billedByCategory.map((category) => (
                <BarRow key={category.feeCategoryId} label={category.categoryName}
                        value={category.billed} max={maxCategory}
                        formatted={formatMoney(category.billed, currency)} />
              ))
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="How families pay"
                      description="By value, not by count — one bank transfer outweighs many cash payments." />
          <div className="px-4 py-3">
            {d.paymentMethods.length === 0 ? (
              <EmptyState title="No payments yet" />
            ) : (
              d.paymentMethods.map((method) => (
                <BarRow key={method.method}
                        label={method.method.replaceAll('_', ' ').toLowerCase()}
                        sublabel={`${method.count} payment${method.count === 1 ? '' : 's'} · ${formatRate(method.percentageOfTotal)}`}
                        value={method.amount} max={maxMethod}
                        formatted={formatMoney(method.amount, currency)} />
              ))
            )}
          </div>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader title="Recent payments"
                    action={<Link to={ROUTES.payments}
                                  className="text-[13px] text-accent hover:underline">All payments</Link>} />
        {d.recentPayments.length === 0 ? (
          <EmptyState title="No payments recorded"
                      description="Payments appear here as they are taken." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  {['Receipt', 'Student', 'Date', 'Method'].map((h) => (
                    <th key={h} scope="col"
                        className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                      {h}
                    </th>
                  ))}
                  <th scope="col"
                      className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-subtle">
                    Amount
                  </th>
                  <th scope="col"
                      className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {d.recentPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-4 py-2.5">
                      <Link to={ROUTES.paymentDetail(payment.id)}
                            className="font-mono text-xs text-accent hover:underline">
                        {payment.receiptNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-ink">{payment.studentName ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums text-muted">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="px-4 py-2.5 text-muted">
                      {payment.paymentMethod.replaceAll('_', ' ').toLowerCase()}
                    </td>
                    <td className="px-4 py-2.5"><Money amount={payment.amount} bold /></td>
                    <td className="px-4 py-2.5"><PaymentStatusBadge status={payment.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
